import {
  contractsListQueryKey,
  CountriesListListData,
  countriesListListInfiniteOptions,
  CountryReadable,
  invoicesContractorsCreateMutation,
  leadsListQueryKey,
  PaginatedDropdownList
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { Col, Form, Input, Modal, notification, Row } from 'antd'

import { ModalProps } from 'antd/lib'
import { Dispatch, SetStateAction } from 'react'

type Props = {
  open: boolean
  onClose: () => void
  setContractorId?: Dispatch<SetStateAction<number | undefined>>
} & ModalProps

const formName = 'add-contactor-form'

export const AddContractorModal = ({
  open,
  onClose,
  setContractorId,
  ...props
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutate, isPending } = useMutation({
    ...invoicesContractorsCreateMutation(),
    onError: (error) => showApiErrors(error, form),
    onSuccess: (data) => {
      setContractorId?.(data?.id)
      invalidateQueries([leadsListQueryKey(), contractsListQueryKey()])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
      form.resetFields()
      onClose()
    }
  })

  const onFormFinish = async () => {
    const values = await form.validateFields()
    mutate({ body: values })
  }

  return (
    <Modal
      open={open}
      maskClosable={false}
      title={t('billing.new_contractor')}
      cancelText={t('buttons.cancel')}
      onCancel={onClose}
      okText={t('buttons.save')}
      okButtonProps={{ htmlType: 'submit', form: formName, loading: isPending }}
      {...props}
    >
      <Form
        name={formName}
        form={form}
        onFinish={onFormFinish}
        layout="vertical"
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="name" label={t('billing.contractor_name')}>
              <Input placeholder={t('billing.contractor_name')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={t('billing.country')} name="country">
              <InfiniteSelect<
                CountriesListListData,
                PaginatedDropdownList,
                CountryReadable
              >
                popupMatchSelectWidth
                placeholder={t('billing.country')}
                getQueryFunction={countriesListListInfiniteOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item label={t('billing.address')} name="address">
              <Input placeholder={t('billing.address')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
